import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { templatesAPI, ConsentTemplateCreate } from '../services/api';
import { 
  Save, 
  ArrowLeft, 
  FileText, 
  Building2, 
  Calendar,
  FileCode,
  AlertCircle,
  Info,
  Copy,
  CheckCircle,
  Scissors,
  AlertTriangle,
  FileCheck,
  CheckSquare,
  XCircle,
  Sparkles
} from 'lucide-react';

const TemplateCreator: React.FC = () => {
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId?: string }>();

  const isEditing = !!templateId;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copiedVariable, setCopiedVariable] = useState<string | null>(null);
  const [generatingAI, setGeneratingAI] = useState(false);
  
  // Estado del formulario
  const [formData, setFormData] = useState<ConsentTemplateCreate>({
    title: '',
    hospital_name: 'E.S.E. HOSPITAL DIVINO SALVADOR DE SOPO',
    hospital_nit: '860.023.878-9',
    document_type: 'FORMATO',
    subprocess: 'ODONTOLOGIA',
    code: 'CG-F-7',
    page_number: '1 de 2',
    revision_date: new Date().toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    version: '01',
    consent_text: `Yo @Nombre identificado(a) con documento N° @Documento en calidad @RolPersona, en pleno uso de mis facultades mentales, libre y consciente, declaro que he sido suficientemente informado(a) en términos claros y comprensibles por @NombreProfesional en calidad de @RolProfesional acerca del procedimiento a realizar.

DECLARO QUE:

1. Me ha sido explicada la naturaleza, el propósito, las complicaciones, los malestares y los posibles riesgos del procedimiento.

2. Comprendo que los resultados no pueden garantizarse.

3. Autorizo, si durante el procedimiento se presentan situaciones no previstas, la realización de procedimientos adicionales que el profesional considere necesarios para mi bienestar.

4. Eximo de responsabilidad al profesional y a la institución si no sigo las recomendaciones dadas, y asumo la responsabilidad de cubrir los costos de tratamientos excluidos del Plan de Salud.

5. Autorizo a los designados a realizarme el siguiente procedimiento:`,
    // Sección de procedimientos
    procedure_title: '',
    procedure_description: '',
    benefits: '',
    risks_complications: '',
    alternatives: '',
    implications: '',
    recommendations: '',
    // Sección de aceptación
    consent_acceptance_text: 'Comprendo y acepto las implicaciones del presente consentimiento informado.',
    digital_authorization_text: 'Autorizo, igualmente a diligenciamiento digital del presente consentimiento',
    // Sección de revocatoria
    revocation_text: 'Por medio de la presente constancia, en pleno uso de mis facultades mentales, manifiesto en forma libre mi negación, para la realización los procedimientos anteriormente mencionados, he decidido no permitir la realización de los procedimientos en mención, voluntariamente haciendo uso de mi derecho a decidir libremente, sin que esto ocasione ningún tipo de represalia contra mí. De igual forma, declaro que he sido debidamente informado(a) y he comprendido los beneficios que trae la realización de los procedimientos para mi salud, así como los riesgos y posibles complicaciones de salud que implica mi negación voluntaria al procedimiento. Afirmo que bajo mi responsabilidad decido desistir y en consecuencia declaro que ni la institución ni su personal serán responsables en caso de complicaciones.'
  });

  // Cargar plantilla si estamos editando
  useEffect(() => {
    if (isEditing && templateId) {
      loadTemplate();
    }
  }, [templateId, isEditing]);

  const loadTemplate = async () => {
    if (!templateId) return;
    
    setLoading(true);
    setError('');
    
    try {
      const template = await templatesAPI.getById(templateId);
      setFormData({
        title: template.title,
        hospital_name: template.hospital_name,
        hospital_nit: template.hospital_nit,
        document_type: template.document_type,
        subprocess: template.subprocess,
        code: template.code,
        page_number: template.page_number,
        revision_date: template.revision_date || '',
        version: template.version,
        consent_text: template.consent_text,
        procedure_title: template.procedure_title || '',
        procedure_description: template.procedure_description || '',
        benefits: template.benefits || '',
        risks_complications: template.risks_complications || '',
        alternatives: template.alternatives || '',
        implications: template.implications || '',
        recommendations: template.recommendations || '',
        consent_acceptance_text: template.consent_acceptance_text || '',
        digital_authorization_text: template.digital_authorization_text || '',
        revocation_text: template.revocation_text || ''
      });
    } catch (err: any) {
      setError('Error al cargar la plantilla: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('El título es obligatorio');
      return;
    }
    
    if (!formData.consent_text.trim()) {
      setError('El texto del consentimiento es obligatorio');
      return;
    }
    
    setSaving(true);
    setError('');
    
    try {
      if (isEditing && templateId) {
        await templatesAPI.update(templateId, formData);
      } else {
        await templatesAPI.create(formData);
      }
      navigate('/templates');
    } catch (err: any) {
      setError('Error al guardar la plantilla: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: keyof ConsentTemplateCreate, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Variables disponibles para usar en el texto
  const availableVariables = [
    { variable: '@Nombre', description: 'Nombre completo del paciente' },
    { variable: '@Documento', description: 'Número de documento del paciente' },
    { variable: '@Sexo', description: 'Sexo del paciente' },
    { variable: '@FechaNacimiento', description: 'Fecha de nacimiento del paciente' },
    { variable: '@Edad', description: 'Edad del paciente' },
    { variable: '@Telefono', description: 'Teléfono del paciente' },
    { variable: '@TipoRegimen', description: 'Tipo de régimen (Contributivo/Subsidizado)' },
    { variable: '@Aseguradora', description: 'Nombre de la EPS/Aseguradora' },
    { variable: '@RolPersona', description: 'Rol de la persona (Paciente/Tutor/Representante Legal)' },
    { variable: '@NombreProfesional', description: 'Nombre del profesional que informa' },
    { variable: '@RolProfesional', description: 'Rol del profesional (Médico/Odontólogo/etc.)' },
    { variable: '@Fecha', description: 'Fecha actual (DD/MM/YYYY)' },
    { variable: '@Hora', description: 'Hora actual' },
  ];

  const copyVariable = (variable: string) => {
    navigator.clipboard.writeText(variable);
    setCopiedVariable(variable);
    setTimeout(() => setCopiedVariable(null), 2000);
  };

  const handleGenerateWithAI = async () => {
    const procedureTitle = formData.procedure_title?.trim() || '';
    
    if (!procedureTitle) {
      setError('Por favor, ingresa un título del procedimiento en la sección "Descripción del Procedimiento" primero.');
      return;
    }

    console.log('Generando con IA. Título del procedimiento:', procedureTitle);

    setGeneratingAI(true);
    setError('');

    try {
      const generatedContent = await templatesAPI.generateContent(procedureTitle);
      
      // Actualizar los campos con el contenido generado
      setFormData(prev => ({
        ...prev,
        procedure_title: generatedContent.procedure_title || prev.procedure_title,
        procedure_description: generatedContent.procedure_description || prev.procedure_description,
        benefits: generatedContent.benefits || prev.benefits,
        risks_complications: generatedContent.risks_complications || prev.risks_complications,
        alternatives: generatedContent.alternatives || prev.alternatives,
        implications: generatedContent.implications || prev.implications,
        recommendations: generatedContent.recommendations || prev.recommendations
      }));
    } catch (err: any) {
      setError('Error al generar contenido con IA: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGeneratingAI(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[45vh] flex-col items-center justify-center py-16">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-2 border-slate-200 border-t-blue-700" />
          <p className="font-medium text-slate-600">Cargando plantilla…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in space-y-6 pb-4">
      <div className="max-w-6xl mx-auto">
        <div className="page-hero mb-2">
          <button type="button" onClick={() => navigate('/templates')} className="btn-secondary mb-4 py-2 text-sm">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Volver a plantillas
          </button>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-blue-700">
            {isEditing ? 'Edición' : 'Nueva plantilla'}
          </p>
          <h1 className="mb-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            {isEditing ? 'Editar plantilla de consentimiento' : 'Crear plantilla de consentimiento'}
          </h1>
          <p className="max-w-2xl text-sm text-slate-600 sm:text-base">
            {isEditing
              ? 'Ajuste el texto legal, metadatos del documento y secciones del procedimiento.'
              : 'Defina encabezado, cuerpo del consentimiento y bloques de procedimiento para uso institucional.'}
          </p>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Sección: Header del Documento */}
          <div className="card">
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-800">Encabezado del Documento</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Columna Izquierda */}
              <div className="space-y-4">
                <div>
                  <label className="label">
                    <Building2 className="w-4 h-4 inline mr-2 text-blue-600" />
                    Nombre del Hospital
                  </label>
                  <input
                    type="text"
                    value={formData.hospital_name}
                    onChange={(e) => handleChange('hospital_name', e.target.value)}
                    className="input-field"
                    placeholder="E.S.E. HOSPITAL DIVINO SALVADOR DE SOPO"
                  />
                </div>

                <div>
                  <label className="label">
                    <FileCode className="w-4 h-4 inline mr-2 text-blue-600" />
                    NIT del Hospital
                  </label>
                  <input
                    type="text"
                    value={formData.hospital_nit}
                    onChange={(e) => handleChange('hospital_nit', e.target.value)}
                    className="input-field"
                    placeholder="860.023.878-9"
                  />
                </div>

                <div>
                  <label className="label">
                    <FileText className="w-4 h-4 inline mr-2 text-blue-600" />
                    Título del Consentimiento *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    className="input-field"
                    placeholder="CONSENTIMIENTO INFORMADO DE PROCEDIMIENTOS ODONTOLÓGICOS PULPOCTOMIA PULPECTOMIA"
                    required
                  />
                </div>
              </div>

              {/* Columna Derecha - Metadatos */}
              <div className="space-y-4">
                <div>
                  <label className="label">
                    Tipo de Documento
                  </label>
                  <input
                    type="text"
                    value={formData.document_type}
                    onChange={(e) => handleChange('document_type', e.target.value)}
                    className="input-field"
                    placeholder="FORMATO"
                  />
                </div>

                <div>
                  <label className="label">
                    Subproceso
                  </label>
                  <input
                    type="text"
                    value={formData.subprocess}
                    onChange={(e) => handleChange('subprocess', e.target.value)}
                    className="input-field"
                    placeholder="ODONTOLOGIA"
                  />
                </div>

                <div>
                  <label className="label">
                    Código
                  </label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => handleChange('code', e.target.value)}
                    className="input-field"
                    placeholder="CG-F-7"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label">
                      Página
                    </label>
                    <input
                      type="text"
                      value={formData.page_number}
                      onChange={(e) => handleChange('page_number', e.target.value)}
                      className="input-field"
                      placeholder="1 de 2"
                    />
                  </div>

                  <div>
                    <label className="label">
                      Versión
                    </label>
                    <input
                      type="text"
                      value={formData.version}
                      onChange={(e) => handleChange('version', e.target.value)}
                      className="input-field"
                      placeholder="01"
                    />
                  </div>
                </div>

                <div>
                  <label className="label">
                    <Calendar className="w-4 h-4 inline mr-2 text-blue-600" />
                    Fecha de Revisión
                  </label>
                  <input
                    type="text"
                    value={formData.revision_date}
                    onChange={(e) => handleChange('revision_date', e.target.value)}
                    className="input-field"
                    placeholder="25/07/2024"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sección: Datos del Paciente (Información fija) */}
          <div className="card bg-blue-50">
            <div className="flex items-center gap-3 mb-4">
              <FileText className="w-6 h-6 text-blue-600" />
              <h2 className="text-xl font-bold text-gray-800">Datos del Paciente</h2>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Estos campos son fijos y se llenarán automáticamente con la información del paciente desde Citisalud:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 bg-white rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-gray-700">Nº DE IDENTIFICACIÓN</p>
              </div>
              <div className="p-3 bg-white rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-gray-700">NOMBRE</p>
              </div>
              <div className="p-3 bg-white rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-gray-700">SEXO</p>
              </div>
              <div className="p-3 bg-white rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-gray-700">FECHA DE NACIMIENTO</p>
              </div>
              <div className="p-3 bg-white rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-gray-700">EDAD</p>
              </div>
              <div className="p-3 bg-white rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-gray-700">TELEFONO</p>
              </div>
              <div className="p-3 bg-white rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-gray-700">TIPO REGIMEN</p>
              </div>
              <div className="p-3 bg-white rounded-lg border border-blue-200">
                <p className="text-sm font-semibold text-gray-700">ASEGURADORA</p>
              </div>
            </div>
          </div>

          {/* Sección: Texto del Consentimiento */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-800">
                  I. CONSENTIMIENTO INFORMADO DE PROCEDIMIENTOS MENORES REALIZADOS POR ODONTOLOGIA
                </h2>
              </div>
            </div>

            {/* Panel de Variables Disponibles */}
            <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-start gap-3 mb-3">
                <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-800 mb-2">Variables Disponibles</h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Haz clic en cualquier variable para copiarla. Luego pégalas en el texto del consentimiento donde necesites que se rellene automáticamente con información del paciente.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {availableVariables.map((item) => (
                      <button
                        key={item.variable}
                        type="button"
                        onClick={() => copyVariable(item.variable)}
                        className="flex items-center justify-between p-2 bg-white rounded-lg border border-blue-200 hover:border-blue-400 hover:bg-blue-50 transition-colors duration-200 text-left group"
                      >
                        <div className="flex-1 min-w-0">
                          <code className="text-xs font-mono font-semibold text-blue-600 block truncate">
                            {item.variable}
                          </code>
                          <span className="text-xs text-gray-600 block truncate">
                            {item.description}
                          </span>
                        </div>
                        {copiedVariable === item.variable ? (
                          <CheckCircle className="w-4 h-4 text-green-600 ml-2 flex-shrink-0" />
                        ) : (
                          <Copy className="w-4 h-4 text-gray-400 group-hover:text-blue-600 ml-2 flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                  {copiedVariable && (
                    <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded-lg text-sm text-green-800">
                      <CheckCircle className="w-4 h-4 inline mr-2" />
                      Variable <code className="font-mono font-semibold">{copiedVariable}</code> copiada. Pégalo en el texto del consentimiento.
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <label className="label">
                Texto del Consentimiento *
              </label>
              <textarea
                value={formData.consent_text}
                onChange={(e) => handleChange('consent_text', e.target.value)}
                className="input-field min-h-[400px] resize-y font-mono text-sm"
                placeholder="Ingresa el texto del consentimiento. Usa variables como @Nombre, @Documento, etc. para campos que se llenarán automáticamente..."
                required
              />
              <p className="text-sm text-gray-500 mt-2">
                💡 <strong>Tip:</strong> Usa variables como <code className="bg-blue-100 px-1 rounded">@Nombre</code>, <code className="bg-blue-100 px-1 rounded">@Documento</code>, etc. para indicar campos que se llenarán automáticamente con la información del paciente desde Citisalud.
              </p>
            </div>
          </div>

          {/* Sección: Procedimientos */}
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Scissors className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-bold text-gray-800">Descripción del Procedimiento</h2>
              </div>
              <button
                type="button"
                onClick={handleGenerateWithAI}
                disabled={generatingAI || !formData.title.trim()}
                className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generatingAI ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Generando...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Llenado con IA
                  </>
                )}
              </button>
            </div>

            <div className="space-y-6">
              {/* Título del procedimiento */}
              <div>
                <label className="label">
                  Título del Procedimiento *
                </label>
                <input
                  type="text"
                  value={formData.procedure_title}
                  onChange={(e) => handleChange('procedure_title', e.target.value)}
                  className="input-field"
                  placeholder="PULPOTOMÍA Y PULPECTOMÍA"
                  required
                />
              </div>

              {/* Descripción del procedimiento */}
              <div>
                <label className="label">
                  Descripción del Procedimiento *
                </label>
                <textarea
                  value={formData.procedure_description}
                  onChange={(e) => handleChange('procedure_description', e.target.value)}
                  className="input-field min-h-[120px] resize-y"
                  placeholder="Remoción del tejido nervioso parcial (pulpotomía) o total (pulpectomía) que se lleva a cabo tanto en la corona como en las raíces de los dientes temporales o permanentes."
                  required
                />
              </div>

              {/* Beneficios */}
              <div>
                <label className="label flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Beneficios *
                </label>
                <textarea
                  value={formData.benefits}
                  onChange={(e) => handleChange('benefits', e.target.value)}
                  className="input-field min-h-[120px] resize-y"
                  placeholder="Disminución del dolor.&#10;Disminución de la probabilidad de infección, conservación de diente y/o molar."
                  required
                />
                <p className="text-sm text-gray-500 mt-2">
                  Lista los beneficios del procedimiento. Puedes escribir uno por línea.
                </p>
              </div>

              {/* Riesgos y Complicaciones */}
              <div>
                <label className="label flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  Riesgos y/o Complicaciones *
                </label>
                <textarea
                  value={formData.risks_complications}
                  onChange={(e) => handleChange('risks_complications', e.target.value)}
                  className="input-field min-h-[200px] resize-y"
                  placeholder="Presencia de curvaturas o calcificaciones dentarias.&#10;Fractura radicular o coronal.&#10;Debilidad del diente con tendencia a fractura..."
                  required
                />
                <p className="text-sm text-gray-500 mt-2">
                  Lista los riesgos y complicaciones del procedimiento. Puedes escribir uno por línea.
                </p>
              </div>

              {/* Alternativas */}
              <div>
                <label className="label flex items-center gap-2">
                  <FileCheck className="w-4 h-4 text-blue-600" />
                  Alternativas *
                </label>
                <textarea
                  value={formData.alternatives}
                  onChange={(e) => handleChange('alternatives', e.target.value)}
                  className="input-field min-h-[100px] resize-y"
                  placeholder="Endodoncia.&#10;Exodoncia."
                  required
                />
                <p className="text-sm text-gray-500 mt-2">
                  Lista las alternativas al procedimiento. Puedes escribir una por línea.
                </p>
              </div>

              {/* Implicaciones */}
              <div>
                <label className="label">
                  Implicaciones *
                </label>
                <textarea
                  value={formData.implications}
                  onChange={(e) => handleChange('implications', e.target.value)}
                  className="input-field min-h-[200px] resize-y"
                  placeholder="Instrucciones postoperatorias y efectos secundarios..."
                  required
                />
                <p className="text-sm text-gray-500 mt-2">
                  Incluye instrucciones postoperatorias y efectos secundarios del procedimiento.
                </p>
              </div>

              {/* Recomendaciones */}
              <div>
                <label className="label">
                  Recomendaciones *
                </label>
                <textarea
                  value={formData.recommendations}
                  onChange={(e) => handleChange('recommendations', e.target.value)}
                  className="input-field min-h-[200px] resize-y"
                  placeholder="Recomendaciones para el paciente después del procedimiento..."
                  required
                />
                <p className="text-sm text-gray-500 mt-2">
                  Recomendaciones específicas que debe seguir el paciente.
                </p>
              </div>
            </div>
          </div>

          {/* Sección: Aceptación */}
          <div className="card bg-green-50">
            <div className="flex items-center gap-3 mb-6">
              <CheckSquare className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-bold text-gray-800">Aceptación del Consentimiento</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="label">
                  Texto de Aceptación del Consentimiento Informado *
                </label>
                <textarea
                  value={formData.consent_acceptance_text}
                  onChange={(e) => handleChange('consent_acceptance_text', e.target.value)}
                  className="input-field min-h-[80px] resize-y"
                  placeholder="Comprendo y acepto las implicaciones del presente consentimiento informado."
                  required
                />
              </div>

              <div>
                <label className="label">
                  Texto de Autorización Digital *
                </label>
                <textarea
                  value={formData.digital_authorization_text}
                  onChange={(e) => handleChange('digital_authorization_text', e.target.value)}
                  className="input-field min-h-[80px] resize-y"
                  placeholder="Autorizo, igualmente a diligenciamiento digital del presente consentimiento"
                  required
                />
              </div>
            </div>
          </div>

          {/* Sección: Revocatoria del Consentimiento */}
          <div className="card bg-orange-50">
            <div className="flex items-center gap-3 mb-6">
              <XCircle className="w-6 h-6 text-orange-600" />
              <h2 className="text-xl font-bold text-gray-800">II. REVOCATORIA DEL CONSENTIMIENTO</h2>
            </div>

            <div>
              <label className="label">
                Texto de Revocatoria del Consentimiento *
              </label>
              <textarea
                value={formData.revocation_text}
                onChange={(e) => handleChange('revocation_text', e.target.value)}
                className="input-field min-h-[200px] resize-y"
                placeholder="Por medio de la presente constancia, en pleno uso de mis facultades mentales, manifiesto en forma libre mi negación..."
                required
              />
              <p className="text-sm text-gray-500 mt-2">
                Este texto será utilizado para la sección de revocatoria del consentimiento informado.
              </p>
            </div>
          </div>

          {/* Mensaje de error */}
          {error && (
            <div className="card bg-red-50 border-red-200">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-red-800 font-medium">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Botones de acción */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/templates')}
              className="btn-secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn-primary"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Guardando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-5 h-5" />
                  {isEditing ? 'Actualizar Plantilla' : 'Crear Plantilla'}
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateCreator;
